import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import {
  STAGE_1_APPROVE_COMPLIANCE_CHECK_VALUE,
  STAGE_1_APPROVE_VALUE,
  STAGE_2_APPROVE_VALUE,
  STAGE_APPROVED_VALUE,
} from "../../../constants/app";
import { useImpersonation } from "../../../contexts/ImpersonationContext";
import { useMissingDocs } from "../../../hooks/useMissingDocs";
import { useAppSelector, useAppSelector as useSelector } from "../../../store";
import {
  TCandidateProfileStesps,
  TCompletionStatus,
} from "../../../store/onboarding/types";
import { getLatestStage2Action } from "../../../utils/stage2ActionHelper";
import Warning from "../../UI/Icons/Warning";
import ProgressBar from "../../UI/ProgressBar";
import "./MyProfile.scss";

const stage1Keys = [
  "id_verification",
  "personal_details",
  "qualification",
  "jobpreference",
  "online_interview",
];
const stage2Keys = [
  "working_with_children",
  "police_check",
  "right_to_work",
  "reference_check",
];
const stage3Keys = [
  "tfn_form",
  "superannuation_form",
  "contract_form",
  "casual_fair_work_statement", 
  "bankAccount",
  "emergency_info",
];

const MyProfile: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeStage, setActiveStage] = useState<string | null>("");
  const [navigateTo, setNavigateTo] = useState<string>("");
  const [stage1CompletionPercentage, setStage1CompletionPercentage] = useState<string | null>('0');
  const [stage2CompletionPercentage, setStage2CompletionPercentage] = useState<string | null>('0');
  const [stage3CompletionPercentage, setStage3CompletionPercentage] = useState<string | null>('0');
  const missingDocs = useMissingDocs();

  const {isImpersonating} = useImpersonation();
  const stepCompletion: TCompletionStatus | null = useSelector(
    (state) => state.onboarding.completedstatus
  ); 

  const candidateProfileStages = useAppSelector((state) => state.onboarding.candidateProfileSteps)
  const profileData = useAppSelector((state) => state.onboarding.personaldetails)
  const userpersonaldetails = useSelector((state) => state.onboarding.userpersonaldetails)
  const personaldetails = useSelector(
    (state) => state.onboarding.personaldetails
  );
  const reopenedStagesStatus = useSelector((state) => state.onboarding.reopenedStagesStatus);

  const currentStage = profileData?.current_stage ? profileData?.current_stage : null;
  const currentReviewStatus = profileData?.review_status;
  const connectIdData = userpersonaldetails?.connect_id_data
    ? userpersonaldetails?.connect_id_data
    : null;

  const profileSteps = userpersonaldetails?.profileSteps;
  const isAdminCompleteSelected = profileSteps?.find(
    (step) => step.action === "approve_candidate_bypass_compliance_check"
  )
  const isAdminCompleteApproved = profileSteps?.find(
    (step) => step.action === "approve_candidate_to_stage_3"
  );

  const isAdminCompleted = getLatestStage2Action(userpersonaldetails?.profileSteps || [], userpersonaldetails?.reopened_stages_status, userpersonaldetails?.referoo_candidate?.created_at
 || null );


  const stage1Values = useMemo(() => {
    return stepCompletion
      ? Object.keys(stepCompletion)
          .filter((key) => stage1Keys.includes(key))
          .map((key) => stepCompletion[key as keyof TCompletionStatus])
      : []; // Default to an empty array if stepCompletion is null
  }, [stepCompletion]);

  const stage2Values = useMemo(() => {
    return stepCompletion
      ? Object.keys(stepCompletion)
          .filter((key) => stage2Keys.includes(key))
          .map((key) => stepCompletion[key as keyof TCompletionStatus])
      : []; // Default to an empty array if stepCompletion is null
  }, [stepCompletion]);

  const stage3Values = useMemo(() => {
    return stepCompletion
      ? Object.keys(stepCompletion)
          .filter((key) => stage3Keys.includes(key))
          .map((key) => stepCompletion[key as keyof TCompletionStatus])
      : []; // Default to an empty array if stepCompletion is null
  }, [stepCompletion]);

   const isPersonalDetailsHaveResume = useMemo(() => {
    if(personaldetails?.resume === null || personaldetails === null) {
      return true;
    }
    return false;
  }, [personaldetails]);

  const checkStageSubmittedOrNot = useCallback((stageNumber: number | null) => {
    if(currentStage === stageNumber){
      return true;
    } else{
      return false;
    } 
  }, [currentStage]);

  const checkStageApprovedOrNot = useCallback((stageNumber: number | null) => {
    const stageData = candidateProfileStages.find((stage) => stage.step_number === stageNumber);
    
    return stageData?.step_number === stageNumber ? true : false;
  }, [candidateProfileStages]);

  const calculateStageCompletionPercentage = useCallback((stageId: string | null) => {
    let result: string = "0";
    if (stageId === "stage1") {
      const totalSteps: number = 5;
      const profileCompletedStatus = stage1Values;
      let completedSteps = Object.values(profileCompletedStatus).reduce(
        (acc: number, curr: number) => acc + curr,
        0
      );
      
      // Adjust completed steps if personal details doesn't have resume
      if (isPersonalDetailsHaveResume && stepCompletion?.personal_details === 1) {
        completedSteps = completedSteps - 1; // Subtract 1 if personal details is marked complete but missing resume
      }

      if(stepCompletion?.qualification === 1 && missingDocs.length !== 0) {
        completedSteps = completedSteps - 1;
      }

      result = String((completedSteps / totalSteps) * 100) ?? 0;
      setStage1CompletionPercentage(result);
    } else if (stageId === "stage2") {
      const totalSteps: number = 4;
      const referroCompletedStatus = stage2Values;
      const completedSteps: any = Object.values(referroCompletedStatus).reduce(
        (acc: number, curr: any) => acc + curr,
        0
      );
      result = String((completedSteps / totalSteps) * 100);
      if(userpersonaldetails?.referoo_status === "pending" || userpersonaldetails?.referoo_status === "in_progress"){
        result = "50";
      }else if(userpersonaldetails?.referoo_status === "complete"){
        result = "100";
      }
      setStage2CompletionPercentage(result);
    } else if (stageId === "stage3") {
      const totalSteps: number = 6;
      const formsCompletedStatus = stage3Values;
      const completedSteps = Object.values(formsCompletedStatus).reduce(
        (acc: number, curr: number) => acc + curr,
        0
      );
      result = String(Math.round((completedSteps / totalSteps) * 100));
      setStage3CompletionPercentage(result);
    }
  }, [stage1Values, stage2Values, stage3Values, stepCompletion, userpersonaldetails, isPersonalDetailsHaveResume, missingDocs]);

  const isStageThreeActivated = (candidateProfileStages: TCandidateProfileStesps[]): boolean => {
    return candidateProfileStages.some(
      (stage) => stage.step_number === 3 && stage.action === "activate"
    );
  };

  useEffect(() => {
    if(activeStage === 'stage1'){
      if(checkStageSubmittedOrNot(1)) {
        if(checkStageApprovedOrNot(1)) {
          if(navigateTo && navigateTo !== '') {
            navigate(`/candidate/my-profile/${navigateTo}`);
          }
        }else {
          navigateTo === "" && navigate('/candidate/my-profile/online-interview')
        }
      }
      //  else if(checkStageSubmittedOrNot(2)){

      // } else {
      //   navigate('/candidate/my-profile/id-verification')
      // }
      
    } else if(activeStage === 'stage2'){
       navigateTo === "" && navigate('/candidate/my-profile/complaince-check')
    } else if(activeStage === 'stage3'){
      if(isStageThreeActivated(candidateProfileStages)){
        navigate('/candidate/my-profile/completed')
      } else if(checkStageSubmittedOrNot(3)) {
        checkStageApprovedOrNot(3) && navigate('/candidate/my-profile/tfn-declaration')
      } else {
        if(stepCompletion?.tfn_form === 0){
          navigate('/candidate/my-profile/tfn-declaration')
        } else if(stepCompletion?.superannuation_form === 0){
          navigate('/candidate/my-profile/superannuation')
        } else if(stepCompletion?.contract_form === 0){
          navigate('/candidate/my-profile/contract-form')
        } else if(stepCompletion?.casual_fair_work_statement === 0){
          navigate('/candidate/my-profile/casual-and-fair-work-statement')
        } else if(stepCompletion?.bankAccount === 0){
          navigate('/candidate/my-profile/bank-account')
        } else if(stepCompletion?.emergency_info === 0){
          navigate('/candidate/my-profile/next-of-kin')
        }
      }
    } else {
      if(activeStage === "stage2") {
        checkStageApprovedOrNot(2) && navigate('/candidate/my-profile/complaince-check')
      } else if(activeStage === "stage3") {
        navigate('/candidate/my-profile/tfn-declaration')
      } else {
        if(checkStageApprovedOrNot(1) && checkStageApprovedOrNot(2)) {
          navigate('/candidate/my-profile/tfn-declaration')
        } else {
          navigate('/candidate/my-profile/id-verification')
        }
      }
    }

    calculateStageCompletionPercentage(activeStage || "stage1");

    sessionStorage.setItem(
      "stage",
      activeStage !== null ? activeStage : "stage1"
    );
  }, [activeStage, stepCompletion, checkStageSubmittedOrNot, calculateStageCompletionPercentage, candidateProfileStages]);

  // useEffect(() => {
  //   calculateStageCompletionPercentage(activeStage || "stage1");
  // }, [activeStage, candidateProfileStages, stepCompletion, navigate, calculateStageCompletionPercentage, checkStageSubmittedOrNot]);

  const handleStageClick = (stageId: string) => {
    let stageStatus: boolean | undefined;
    if (stageId === "stage1") {
      stageStatus = isStageOneApproved(1);
    } else if (stageId === "stage2") {
      stageStatus = isStageTwoApproved(2);
    } else if (stageId === "stage3") {
      stageStatus = isStageThreeApproved(3);
    }
    if(stageStatus === false){
      setActiveStage(stageId);
    }

    calculateStageCompletionPercentage(stageId);
  };

  const isStageOneApproved = (stageNumber: number) => {
    if (stageNumber) {
      const reopenData = reopenedStagesStatus?.find((stage) => stage.stage_number === stageNumber);
      const candidateStageData =
        candidateProfileStages &&
        candidateProfileStages.length > 0 &&
        candidateProfileStages.find(
          (stage: TCandidateProfileStesps) => stage.step_number === stageNumber
        ); 

        if(candidateStageData && (candidateStageData.action === STAGE_1_APPROVE_VALUE || candidateStageData.action === STAGE_1_APPROVE_COMPLIANCE_CHECK_VALUE)) {
          if(reopenData && (reopenData.status  === "approved" || reopenData.status  === "resubmitted")) {
            return true;
          } else if(reopenData && reopenData.status  === "resubmitted" && currentStage === 1) {
            return false;
          }
          return true;
        } 
    }
  };

  const isStageTwoApproved = (stageNumber: number) => {
    if (stageNumber) {
      const reopenData = reopenedStagesStatus?.find((stage) => stage.stage_number === stageNumber);
      const candidateStageData =
        candidateProfileStages &&
        candidateProfileStages.length > 0 &&
        candidateProfileStages.find(
          (stage: TCandidateProfileStesps) => stage.step_number === stageNumber
        );

      if(candidateStageData && (candidateStageData.action === STAGE_2_APPROVE_VALUE)) {
        if(reopenData && (reopenData.status  === "approved" || reopenData.status  === "resubmitted")) {
          return true;
        } else if(reopenData && reopenData.status  === "resubmitted" && currentStage === 2) {
          return false;
        }
        return true;
      }
      // if (
      //   candidateStageData &&
      //   candidateStageData.action === STAGE_2_APPROVE_VALUE
      // ) {
      //   return true;
      // } else {
      //   return false;
      // }
    }
  };

  const isStageThreeApproved = (stageNumber: number) => {
    if (stageNumber) {
      const candidateStageData =
        candidateProfileStages &&
        candidateProfileStages.length > 0 &&
        candidateProfileStages.find(
          (stage: TCandidateProfileStesps) => stage.step_number === stageNumber
        );
      if (
        candidateStageData &&
        candidateStageData.action === STAGE_APPROVED_VALUE
      ) {
        return true;
      } else {
        return false;
      }
    }
  };

  // const currentVerificationStatus = profileData?.verification_status;

  useEffect(() => {
    if(currentStage === 1 && currentReviewStatus === 'approved') {
      setActiveStage('stage2')
    }else if(currentStage === 2 && (currentReviewStatus === 'waiting' || (userpersonaldetails?.referoo_status === "pending" && currentReviewStatus !== 'approved'))) {
      setActiveStage('stage2')
    }else if(currentStage === 2 && currentReviewStatus === 'approved') {
      setActiveStage('stage3')
    }else if(currentStage === 3){
      setActiveStage('stage3')
    }else {
      setActiveStage('stage1'); 
    }


    // if(activeStage === 'stage1' && isStageOneApproved(1)) {
    //   setActiveStage('stage1');
    // } else if(activeStage === 'stage2' && isStageTwoApproved(2)) {
    //   setActiveStage('stage2');
    // } else if(activeStage === 'stage3') {
    //   setActiveStage('stage3');
    // } else {
    //   setActiveStage('stage1'); // Default to stage 1 if no active stage is set
    // }
  },[currentStage, currentReviewStatus, userpersonaldetails])



  useEffect(() => {
    checkStageSubmittedOrNot(currentStage)
  }, [candidateProfileStages, currentStage, checkStageSubmittedOrNot])

  return (
      <div className="d-flex flex-column gap-25 max-w-960px flk-form">
        <div className="d-flex">
        <div className="bg-teal-light py-10px px-15px radius-8px d-flex gap-6">
          <Warning />
          {activeStage === "stage1" && (
            <div className="fs-13 fw-400 text-black">
              {(checkStageSubmittedOrNot(1))
                ? "You have submitted your information for Stage 1: Personal Details & Interview and it is in review right now."
                : "You are currently up to Stage 1: Personal Details & Interview. Finish setting up and submit your information for review."}
              <br />
              Please note that between each stage, it will take up to 1 business day
              for a manual review to be completed by our consultants.
            </div>
          )}

          {activeStage === "stage2" && (
            <div className="fs-13 fw-400 text-black">
              {(checkStageSubmittedOrNot(2) || stage2CompletionPercentage === "100")
                ? "You have submitted your information for Stage 2: Compliance Checks and it is in review right now."
                : "You are currently up to Stage 2: Compliance Checks. Finish setting up and submit your information for review."}
              <br />
              Please note that between each stage, it will take up to 1 business day
              for a manual review to be completed by our consultants.
            </div>
          )}

          {activeStage === "stage3" && (
            <div className="fs-13 fw-400 text-black">
              {checkStageSubmittedOrNot(3)
                ? "You have submitted your information for Stage 3: Documents & Contract and it is in review right now."
                : "You are currently up to Stage 3: Documents & Contract. Finish setting up and submit your information for review."}
              <br />
              Please note that between each stage, it will take up to 1 business day
              for a manual review to be completed by our consultants.
            </div>
          )}
        </div>
      </div>


        {/* ProgressBar */}
        {activeStage === "stage1" ? (
          <div className="d-flex flex-column gap-10">
            <div className="fs-16 fw-400 text-grey-medium">
              {stage1CompletionPercentage}% of Stage 1 completed
            </div>
            <ProgressBar
              progress={stage1CompletionPercentage}
              bgColor="bg-off-white"
              progressColor="bg-teal"
            />
          </div>
        ) : activeStage === "stage2" ? (
          <div className="d-flex flex-column gap-10">
            <div className="fs-16 fw-400 text-grey-medium">
              {stage2CompletionPercentage}% of Stage 2 completed
            </div>
            <ProgressBar
              progress={stage2CompletionPercentage}
              bgColor="bg-off-white"
              progressColor="bg-teal"
            />
          </div>
        ) : activeStage === "stage3" ? (
          <div className="d-flex flex-column gap-10">
            <div className="fs-16 fw-400 text-grey-medium">
              {stage3CompletionPercentage}% of Stage 3 completed
            </div>
            <ProgressBar
              progress={stage3CompletionPercentage}
              bgColor="bg-off-white"
              progressColor="bg-teal"
            />
          </div>
        ) : (
          ""
        )}

        <div className="d-flex flex-column flex-lg-row gap-10">
          <div className="onbaording-steps min-w-260px d-flex flex-column gap-20">
            <div className="d-flex flex-column gap-20">
              <div
                className="onboarding-stage cursor-pointer d-flex flex-column gap-8"
                onClick={() => isStageOneApproved(1) ? handleStageClick("stage1") : undefined}
              >
                <div className="d-flex">
                  <div className="bg-teal-muted border-1 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px">
                    Stage 1
                  </div>
                </div>
                <div className="d-flex flex-row flex-md-column gap-8 align-items-center align-items-md-start">
                  <div className="d-flex flex-column">
                    <div className="fs-16 fw-700 text-black lh-1">
                      Personal Details &amp; Interview                    
                    </div>
                    {
                      isStageOneApproved(1) ? (
                        <div className="fs-13 text-teal-dark fw-700">Completed and Approved</div>
                      ) : checkStageSubmittedOrNot(1) ? (
                        <div className="fs-13 text-danger fw-700">Submitted and in Review</div>
                      ): (
                        <div className="fs-13 fw-700 text-grey-dark lh-1">30min</div>
                      )
                    }
                  </div>   
                </div>
                <div className="fs-16 fw-400 text-black d-md-none">
                  Verify ID, upload qualifications, add your availabilities,
                  record a video interview
                </div>
              </div>
              {((activeStage === "stage1" && !isStageOneApproved(1)) || isImpersonating) && (
                <div className="progress-container flex-column top-0">
                  <Link
                    // className={`progress-step ${
                    //   stepCompletion?.id_verification === 1 ? "completed" : ""
                    // } ${
                    //   location.pathname ===
                    //   "/candidate/my-profile/id-verification"
                    //     ? "fw-700"
                    //     : ""
                    // }`}
                    // data-step="1"
                    // to="id-verification"
                    // data-discover="true"
                    className={`progress-step ${
                      stepCompletion?.id_verification === 1 && connectIdData ? "completed" : ""
                    } ${
                      checkStageSubmittedOrNot(1) ? " disabled" : ""
                    } ${
                      location.pathname === "/candidate/my-profile/id-verification" ? " fw-700" : ""
                    }`}
                    data-step="1"
                    to={"id-verification"}
                    data-discover="true"
                    onClick={() => {isImpersonating && setActiveStage("stage1"); setNavigateTo("id-verification")}}
                    // tabIndex={currentStage === 1 && checkStageSubmittedOrNot(1) ? -1 : 0}  
                    // aria-disabled={currentStage === 1 && checkStageSubmittedOrNot(1)}
                    // onClick={
                    //   currentStage === 1 && checkStageSubmittedOrNot(1)
                    //     ? e => e.preventDefault()
                    //     : undefined
                    // }
                  >
                    ID Verification
                  </Link>
                  <Link
                    className={`progress-step ${
                      (stepCompletion?.personal_details === 1 && !isPersonalDetailsHaveResume) ? "completed" : ""
                    } ${
                      checkStageSubmittedOrNot(1) ? " disabled" : ""
                    } ${
                      location.pathname === "/candidate/my-profile/personal-details" ? " fw-700" : ""
                    }`}
                    data-step="2"
                    to={"personal-details"}
                    data-discover="true"
                    onClick={() => {isImpersonating && setActiveStage("stage1"); setNavigateTo("personal-details")}}
                    // tabIndex={stepCompletion?.personal_details === 1 && checkStageSubmittedOrNot(1) ? -1 : 0}  
                    // aria-disabled={stepCompletion?.personal_details === 1 && checkStageSubmittedOrNot(1)}
                    // onClick={
                    //   stepCompletion?.personal_details === 1 && checkStageSubmittedOrNot(1)
                    //     ? e => e.preventDefault()
                    //     : undefined
                    // }
                  >
                    Personal Details
                  </Link>
                  { stepCompletion?.personal_details === 1 ||
                    stepCompletion?.qualification === 1 ? (
                    <Link
                      className={`progress-step ${
                        (stepCompletion?.qualification === 1 && missingDocs.length === 0) ? "completed" : ""
                      } ${
                        checkStageSubmittedOrNot(1) ? " disabled" : ""
                      } ${
                        location.pathname ===
                        "/candidate/my-profile/qualifications"
                          ? "fw-700"
                          : ""
                      }`}
                      data-step="3"
                      to={"qualifications"}
                      data-discover="true"
                      onClick={() => {isImpersonating && setActiveStage("stage1"); setNavigateTo("qualifications")}}
                      // tabIndex={stepCompletion?.qualification === 1 && checkStageSubmittedOrNot(1) ? -1 : 0}
                      // aria-disabled={stepCompletion?.qualification === 1 && checkStageSubmittedOrNot(1)}
                      // onClick={
                      //   stepCompletion?.qualification === 1 && checkStageSubmittedOrNot(1)
                      //     ? e => e.preventDefault()
                      //     : undefined
                      // }
                    >
                      Qualifications
                    </Link> ) : (
                    <div data-step="3" className="progress-step">
                      Qualifications
                    </div>
                  )}
                  { stepCompletion?.qualification === 1 ||
                    stepCompletion?.jobpreference === 1 ? (
                    <Link
                      className={`progress-step ${
                        stepCompletion?.jobpreference === 1  ? "completed" : ""
                      } ${
                        checkStageSubmittedOrNot(1) ? " disabled" : ""
                      } ${
                        location.pathname ===
                        "/candidate/my-profile/job-preferences"
                          ? "fw-700"
                          : ""
                      }`}
                      data-step="4"
                      to={"job-preferences"}
                      data-discover="true"
                      onClick={() => {isImpersonating && setActiveStage("stage1"); setNavigateTo("job-preferences")}}
                      // tabIndex={stepCompletion?.jobpreference === 1 && checkStageSubmittedOrNot(1) ? -1 : 0}
                      // aria-disabled={stepCompletion?.jobpreference === 1 && checkStageSubmittedOrNot(1)}
                      // onClick={
                      //   stepCompletion?.jobpreference === 1 && checkStageSubmittedOrNot(1)
                      //     ? e => e.preventDefault()
                      //     : undefined
                      // }
                    >
                      Job Preferences
                    </Link> ) : (
                    <div data-step="4" className="progress-step">
                      Job Preferences
                    </div>
                  )}
                  { stepCompletion?.jobpreference === 1 ? (
                    <Link
                      className={`progress-step ${
                        stepCompletion?.online_interview === 1  ? "completed" : ""
                      } ${
                        checkStageSubmittedOrNot(1) ? " disabled" : ""
                      } ${
                        location.pathname === "/candidate/my-profile/online-interview" ? "fw-700" : ""
                      }`}
                      data-step="5"
                      to={"online-interview"}
                      data-discover="true"
                      onClick={() => {isImpersonating && setActiveStage("stage1"); setNavigateTo("online-interview")}}
                      // tabIndex={stepCompletion?.online_interview === 1 && checkStageSubmittedOrNot(1) ? -1 : 0}
                      // aria-disabled={stepCompletion?.online_interview === 1 && checkStageSubmittedOrNot(1)}
                      // onClick={
                      //   stepCompletion?.online_interview === 1 && checkStageSubmittedOrNot(1)
                      //     ? e => e.preventDefault()
                      //     : undefined
                      // }
                    >
                      Online Interview
                    </Link> ) : (
                    <div data-step="5" className="progress-step">
                      Online Interview
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="d-flex flex-column gap-20">
              <div
                className="onboarding-stage cursor-pointer d-flex flex-column gap-8"
                onClick={() => { if (isStageTwoApproved(2) || isImpersonating) { setNavigateTo(""); handleStageClick("stage2"); } }}
              >
                <div className="d-flex">
                  <div className="bg-teal-muted border-1 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px">
                    Stage 2
                  </div>
                </div>
                <div className="d-flex flex-row flex-md-column gap-8 align-items-center align-items-md-start">
                  <div className="d-flex flex-column">
                    <div className="fs-16 fw-700 text-black lh-1">
                      Compliance Checks
                    </div>
                    {
                       isAdminCompleted === "admin-complete" ? (
                        <div className="fs-13 text-teal-dark fw-700">Completed by Connected Crew Admin</div>
                      ) : isStageTwoApproved(2) ? (
                        <div className="fs-13 text-teal-dark fw-700"> Completed and Approved</div>
                      ) : (checkStageSubmittedOrNot(2) || stage2CompletionPercentage === "100") ? (
                        <div className="fs-13 text-danger fw-700">Submitted and in Review</div>
                      ): (
                        <div className="fs-13 fw-700 text-grey-dark lh-1">30min</div>
                      )
                    }
                  </div> 
                </div>
              </div>
              {activeStage === "stage2" && (
                <div className="progress-container top-0">
                  <Link
                    className={`progress-step ${
                      (stepCompletion?.police_check === 1 || userpersonaldetails?.referoo_status === "complete") ? "completed" : ""
                    }`}
                    data-step="1"
                    to="#"
                    data-discover="true"
                    aria-disabled={stepCompletion?.police_check === 1 && checkStageSubmittedOrNot(2)}
                    onClick={
                      stepCompletion?.police_check === 1 && checkStageSubmittedOrNot(2)
                        ? e => e.preventDefault()
                        : undefined
                    }
                  >
                    Police Check
                  </Link>
                  <Link
                    className={`progress-step ${
                      (stepCompletion?.working_with_children === 1 || userpersonaldetails?.referoo_status === "complete")
                        ? "completed"
                        : ""
                    }`}
                    data-step="2"
                    to="#"
                    data-discover="true"
                    aria-disabled={stepCompletion?.working_with_children === 1  && checkStageSubmittedOrNot(2)}
                    onClick={
                      stepCompletion?.working_with_children === 1  && checkStageSubmittedOrNot(2)
                        ? e => e.preventDefault()
                        : undefined
                    }
                  >
                    Working With Children
                  </Link>
                  <Link
                    className={`progress-step ${
                      (stepCompletion?.right_to_work === 1 || userpersonaldetails?.referoo_status === "complete") ? "completed" : ""
                    }`}
                    data-step="3"
                    to="#"
                    data-discover="true"
                    aria-disabled={stepCompletion?.right_to_work === 1  && checkStageSubmittedOrNot(2)}
                    onClick={
                      stepCompletion?.right_to_work === 1  && checkStageSubmittedOrNot(2)
                        ? e => e.preventDefault()
                        : undefined
                    }
                  >
                    Right To Work
                  </Link>
                  <Link
                    className={`progress-step ${
                      (stepCompletion?.reference_check === 1 || userpersonaldetails?.referoo_status === "complete") ? "completed" : ""
                    }`}
                    data-step="4"
                    to="#"
                    data-discover="true"
                    aria-disabled={stepCompletion?.reference_check === 1  && checkStageSubmittedOrNot(2)}
                    onClick={
                      stepCompletion?.reference_check === 1
                        ? e => e.preventDefault()
                        : undefined
                    }
                  >
                    Reference Check
                  </Link>
                </div>
              )}
            </div>
            <div className="d-flex flex-column gap-20">
              <div
                className="onboarding-stage cursor-pointer d-flex flex-column gap-8"
                onClick={() => isStageThreeApproved(3) ? handleStageClick("stage3") : undefined}
              >
                <div className="d-flex">
                  <div className="bg-teal-muted border-1 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px">
                    Stage 3
                  </div>
                </div>
                <div className="d-flex flex-row flex-md-column gap-8 align-items-center align-items-md-start">
                  <div className="d-flex flex-column">
                    <div className="fs-16 fw-700 text-black lh-1">
                      Documents &amp; Contract
                    </div>
                    {
                      isStageThreeApproved(3) ? (
                        <div className="fs-13 text-teal-dark fw-700">Completed and Approved</div>
                        // <div className="fs-13 text-danger fw-700">Completed and Approved</div>
                      ) : checkStageSubmittedOrNot(3) ? (
                        <div className="fs-13 text-danger fw-700">Submitted and in Review</div>
                      ): (
                        <div className="fs-13 fw-700 text-grey-dark lh-1">30min</div>
                      )
                    }
                  </div>
                </div>
              </div>
              {((activeStage === "stage3" && !isStageThreeApproved(3)) || (isImpersonating && isStageOneApproved(1) && isStageTwoApproved(2))) && (
                <div className="progress-container flex-column top-0">
                  <Link
                    className={`progress-step ${
                      stepCompletion?.tfn_form === 1 ? "completed" : ""
                    } ${
                      checkStageSubmittedOrNot(3) ? " disabled" : ""
                    }${
                      location.pathname === "/candidate/my-profile/tfn-declaration" ? "fw-700" : ""
                    }`}
                    data-step="1"                    
                    data-discover="true"
                    to={"tfn-declaration"}
                    // aria-disabled={stepCompletion?.tfn_form === 1 && checkStageSubmittedOrNot(3)}
                    // onClick={
                    //   stepCompletion?.tfn_form === 1 && checkStageSubmittedOrNot(3)
                    //     ? e => e.preventDefault()
                    //     : undefined
                    // }
                  >
                    TFN Declaration
                  </Link>
                  { stepCompletion?.tfn_form === 1 ||
                    stepCompletion?.superannuation_form === 1 ? (
                    <Link
                      className={`progress-step ${
                        stepCompletion?.superannuation_form === 1
                          ? "completed"
                          : ""
                      } ${
                        checkStageSubmittedOrNot(3) ? " disabled" : ""
                      } ${
                        location.pathname === "/candidate/my-profile/superannuation" ? "fw-700" : ""
                      }`}
                      data-step="2"
                      data-discover="true"
                      to={"superannuation"}
                      // aria-disabled={stepCompletion?.superannuation_form === 1 && checkStageSubmittedOrNot(3)}
                      // onClick={
                      //   stepCompletion?.superannuation_form === 1 && checkStageSubmittedOrNot(3)
                      //     ? e => e.preventDefault()
                      //     : undefined
                      // }
                    >
                      Superannuation
                    </Link>
                  ) : (
                    <div data-step="2" className="progress-step">
                      Superannuation
                    </div>
                  )}
                  { stepCompletion?.superannuation_form === 1 ||
                    stepCompletion?.contract_form === 1 ? (
                    <Link
                      className={`progress-step ${
                        stepCompletion?.contract_form === 1 ? "completed" : ""
                      } ${
                        checkStageSubmittedOrNot(3) ? " disabled" : ""
                      } ${
                        location.pathname === "/candidate/my-profile/contract-form" ? "fw-700" : ""
                      }`}
                      data-step="3"
                      data-discover="true"
                      to={"contract-form"}
                      // aria-disabled={stepCompletion?.contract_form === 1 && checkStageSubmittedOrNot(3)}
                      // onClick={
                      //   stepCompletion?.contract_form === 1 && checkStageSubmittedOrNot(3)
                      //     ? e => e.preventDefault()
                      //     : undefined
                      // }
                    >
                      Contract
                    </Link> 
                  ) : (
                    <div data-step="3" className="progress-step">
                      Contract
                    </div>
                  )}
                  { stepCompletion?.contract_form === 1 ||
                    stepCompletion?.casual_fair_work_statement === 1 ? (
                    <Link
                      className={`progress-step ${
                        stepCompletion?.casual_fair_work_statement === 1 ? "completed" : ""
                      } ${
                        checkStageSubmittedOrNot(3) ? " disabled" : ""
                      } ${
                        location.pathname === "/candidate/my-profile/casual-and-fair-work-statement" ? "fw-700" : ""
                      }`}
                      data-step="4"
                      data-discover="true"
                      to={"casual-and-fair-work-statement"}
                    >
                      Casual & Fair Work Statement
                    </Link>
                  ) : (
                    <div data-step="4" className="progress-step">
                      Casual & Fair Work Statement
                    </div>
                  )}
                  { stepCompletion?.casual_fair_work_statement === 1 ||
                    stepCompletion?.bankAccount === 1 ? (
                    <Link
                      className={`progress-step ${
                        stepCompletion?.bankAccount === 1 ? "completed" : ""
                      } ${
                        checkStageSubmittedOrNot(3) ? " disabled" : ""
                      } ${
                        location.pathname === "/candidate/my-profile/bank-account" ? "fw-700" : ""
                      }`}
                      data-step="5"
                      data-discover="true"
                      to={"bank-account"}
                      // aria-disabled={stepCompletion?.bankAccount === 1 && checkStageSubmittedOrNot(3)}
                      // onClick={
                      //   stepCompletion?.bankAccount === 1 && checkStageSubmittedOrNot(3)
                      //     ? e => e.preventDefault()
                      //     : undefined
                      // }
                    >
                      Bank Account
                    </Link>
                  ) : (
                    <div data-step="5" className="progress-step">
                      Bank Account
                    </div>
                  )}
                  { stepCompletion?.bankAccount === 1 ||
                    stepCompletion?.emergency_info === 1 ? (
                    <Link
                      className={`progress-step ${
                        stepCompletion?.emergency_info === 1 ? "completed" : ""
                      } ${
                        checkStageSubmittedOrNot(3) ? " disabled" : ""
                      } ${
                        location.pathname === "/candidate/my-profile/next-of-kin" ? "fw-700" : ""
                      }`}
                      data-step="6"
                      data-discover="true"
                      to={"next-of-kin"}
                      // aria-disabled={stepCompletion?.emergency_info === 1 && checkStageSubmittedOrNot(3)}
                      // onClick={
                      //   stepCompletion?.emergency_info === 1 && checkStageSubmittedOrNot(3)
                      //     ? e => e.preventDefault()
                      //     : undefined
                      // }
                    >
                      Next of Kin
                    </Link>) : (
                      <div data-step="6" className="progress-step">
                        Next of Kin
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
          <div className="border-left border-off-white pl-50px w-100 mobile-p-0 mobile-border-0">
            <Outlet />
          </div>
        </div>
      </div>
  );
};

export default MyProfile;
